import { deliveryMatchOrderingExecutor } from 'components/ordering/delivery-match/delivery-match-executor.js'
import {
  PriceData,
  DeliveryMethods,
  DeliveryAddress,
  RefreshShipmentMethodsForBasket,
  UpdateShipmentMethod,
  DeliveryMatchFailure,
  SearchForDropOffLocations,
  type BasketNeedsAtLeastOneProductFailure,
  type DropOffMethods,
} from '@cloudsuite/cs-ordering-defs'
import { is } from 'typia'

type FetcherBody = RefreshShipmentMethodsForBasketBody | UpdateShipmentMethodBody | SearchForDropOffLocationsBody


export type TemplatePriceData = {
  amount_incl_tax: string
  amount_ex_tax: string
}


// needs to match output of webshop.plugins.jinja_filters.to_delivery_match_address
export type TemplateDeliveryAddress = {
  recipient_name: string
  street: string
  address_1: string
  address_2: string | null
  house_number: string
  house_number_addition: string | null
  city: string
  zipcode: string
  country_code: string
  email: string | null
  telephone_number: string | null
}


export type RefreshShipmentMethodsForBasketBody = {
  basketId: string
  address: TemplateDeliveryAddress
  languageCode: string
  currencyCode: string
  shopId: number
  locale: string
  priceData: TemplatePriceData | null
}

export type UpdateShipmentMethodBody = {
  shopId: number
  methodId: string
  basketId: string
}

export type SearchForDropOffLocationsBody = {
  basketId: string
  zipcode: string
  countryCode: string
  shopId: number
}

interface TypedFetcher {
  (commandName: 'RefreshShipmentMethodsForBasket', body?: FetcherBody): Promise<DeliveryMethods>
  (commandName: 'UpdateShipmentMethod', body?: FetcherBody): Promise<void>
  (commandName: 'SearchForDropOffLocations', body?: FetcherBody): Promise<DropOffMethods>
}

export class Fetcher {
  fetch: TypedFetcher = async (commandName: any, body?: any): Promise<any> => {
    const handleRefreshShipmentMethodsForBasket = async () => {
      if (!body) throw new Error('No parameters supplied')
      const _body = body as RefreshShipmentMethodsForBasketBody

      const basketId = _body['basketId']
      const languageCode = _body['languageCode']
      const currencyCode = _body['currencyCode']
      const shopId = _body['shopId']
      const priceData = _body.priceData !== null ? new PriceData(_body.priceData['amount_incl_tax'], _body.priceData['amount_ex_tax']) : null

      const hardCodedRefreshShipmentMethodsForBasket = new RefreshShipmentMethodsForBasket(
        basketId,
        new DeliveryAddress(
          _body.address['recipient_name'],
          _body.address['street'],
          _body.address['address_1'],
          _body.address['address_2'],
          _body.address['house_number'],
          _body.address['house_number_addition'],
          _body.address['city'],
          _body.address['zipcode'],
          _body.address['country_code'],
          _body.address['email'] ?? '',
          _body.address['telephone_number'] ?? '',
        ),
        languageCode,
        currencyCode,
        shopId,
        priceData ?? new PriceData('0', '0'),
      )

      const response = await deliveryMatchOrderingExecutor.execute(hardCodedRefreshShipmentMethodsForBasket)

      if (is<DeliveryMethods>(response)) return response

      if (is<DeliveryMatchFailure>(response)) {
        console.error(`Delivery Match Error: ${response.code} - ${response.message} - ${response.shipment_id}`)
        throw new Error(response.message)
      }

      if (is<BasketNeedsAtLeastOneProductFailure>(response)) {
        throw new Error('Basket needs at least one product')
      }

      return
    }

    const handleUpdateShipmentMethod = async () => {
      if (!body) throw new Error('No parameters supplied')
      const _body = body as UpdateShipmentMethodBody

      const hardCodedUpdateShipmentMethod = new UpdateShipmentMethod(
        _body.shopId,
        _body.methodId,
        _body.basketId
      )

      const response = await deliveryMatchOrderingExecutor.execute(hardCodedUpdateShipmentMethod)

      if (is<DeliveryMatchFailure>(response)) {
        console.error(`Delivery Match Error: ${response.code} - ${response.message} - ${response.shipment_id}`)
        throw response
      }

      return
    }

    const handleSearchForDropOffLocations = async () => {
      if (!body) throw new Error('No parameters supplied')
      const _body = body as SearchForDropOffLocationsBody

      const searchForDropoffLocationsRequest = new SearchForDropOffLocations(
        _body.basketId,
        _body.zipcode,
        _body.countryCode,
        _body.shopId
      )

      const response = await deliveryMatchOrderingExecutor.execute(searchForDropoffLocationsRequest)

      if (is<DeliveryMatchFailure>(response)) {
        console.error(`Delivery Match Error: ${response.code} - ${response.message} - ${response.shipment_id}`)
        throw new Error()
      }

      return response
    }

    if (commandName === 'RefreshShipmentMethodsForBasket')
      return handleRefreshShipmentMethodsForBasket()

    if (commandName === 'UpdateShipmentMethod')
      return handleUpdateShipmentMethod()

    if (commandName === 'SearchForDropOffLocations')
      return handleSearchForDropOffLocations()

    throw new Error('Not Implemented')
  }
}

export const fetcher = new Fetcher()
